/**
 * OnboardingBaselineStep — non-blocking onboarding step that lets a new org
 * "light up" Mission Control instantly by applying a modeled benchmark baseline
 * for their industry × size, then shows the data-completeness meter as the
 * progress indicator toward replacing modeled data with their own.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, Zap, Loader2, CheckCircle2, TrendingUp, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import api from '../../lib/api';
import { toast } from 'sonner';
import DataCompletenessMeter from '../../components/benchmark/DataCompletenessMeter';

export default function OnboardingBaselineStep({ industry, headcount, orgId }) {
  const [preview, setPreview] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);

  const params = useCallback(() => {
    const p = new URLSearchParams();
    if (industry) p.set('industry', industry);
    if (headcount) p.set('headcount', headcount);
    if (orgId) p.set('organization_id', orgId);
    return p.toString();
  }, [industry, headcount, orgId]);

  const compQuery = orgId ? `?organization_id=${orgId}` : '';

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [{ data: pv }, comp] = await Promise.all([
          api.get(`/admin/benchmark-baseline/preview?${params()}`),
          api.get(`/admin/benchmark-baseline/completeness${compQuery}`).catch(() => null),
        ]);
        if (!alive) return;
        setPreview(pv);
        if (comp?.data) { setCompleteness(comp.data); setApplied(comp.data.has_modeled); }
      } catch (e) { /* non-fatal */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [params, compQuery]);

  const apply = async () => {
    setApplying(true);
    try {
      await api.post('/admin/benchmark-baseline/apply', {
        industry, headcount: headcount ? Number(headcount) : undefined,
        organization_id: orgId || undefined,
      });
      const comp = await api.get(`/admin/benchmark-baseline/completeness${compQuery}`);
      setCompleteness(comp.data);
      setApplied(true);
      toast.success('Mission Control is lit up with a modeled baseline');
    } catch (e) {
      toast.error('Could not apply baseline');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="step-baseline-content">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
          <Sparkles className="text-cyan-600" size={20} />
        </div>
        <div>
          <h2 className="font-semibold text-lg text-slate-900">Light up Mission Control</h2>
          <p className="text-sm text-slate-500">Apply a benchmark baseline now, replace it with your own data as you go.</p>
        </div>
      </div>

      <div className="p-4 rounded-xl border border-cyan-200 bg-gradient-to-br from-cyan-50/60 to-white">
        <p className="text-sm text-slate-600">
          No data yet? We can populate the entire value chain with a <strong>defensible modeled baseline</strong>
          {' '}for {preview?.industry_label || 'your industry'}{preview?.size_band_label ? ` · ${preview.size_band_label}` : ''}.
          Every number is sourced and shown as a range, so your dashboards work from day one — and each stream
          you later replace with real data makes them stronger.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm mt-4"><Loader2 className="h-4 w-4 animate-spin" /> Modeling your baseline…</div>
        ) : preview ? (
          <div className="flex flex-wrap items-center gap-6 mt-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-cyan-600" />
              <div>
                <div className="text-xl font-bold text-cyan-700" data-testid="onboard-baseline-lei">{preview.lei.overall}</div>
                <div className="text-[11px] text-slate-500">Overall LEI</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-indigo-600" />
              <div>
                <div className="text-xl font-bold text-indigo-700">{preview.people_engine.pe_composite}</div>
                <div className="text-[11px] text-slate-500">People Engine</div>
              </div>
            </div>
            <Badge className="bg-cyan-50 text-cyan-700 border border-cyan-200 gap-1">
              <Sparkles className="h-3 w-3" /> Modeled · {preview.confidence.level}
            </Badge>
          </div>
        ) : null}

        {!applied ? (
          <Button onClick={apply} disabled={applying || loading} className="mt-4 gap-2 bg-cyan-600 hover:bg-cyan-700" data-testid="onboard-apply-baseline">
            {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
            Apply benchmark baseline
          </Button>
        ) : (
          <div className="mt-4 flex items-center gap-2 text-emerald-700 text-sm font-medium" data-testid="onboard-baseline-applied">
            <CheckCircle2 className="h-4 w-4" /> Baseline applied — your dashboards are populated.
          </div>
        )}
      </div>

      {applied && completeness && (
        <div className="p-4 rounded-xl border border-slate-200 bg-white">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Your data completeness</div>
          <DataCompletenessMeter completeness={completeness} />
        </div>
      )}

      <p className="text-xs text-slate-400">
        This step is optional and fully reversible. You can apply, replace, or clear the modeled baseline any time
        from Data Management → Benchmark Baseline.
      </p>
    </div>
  );
}
